import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form945XInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="General Filing instruction to file form 945-X| TaxZerone ®"
          description="Form 945-X is used to correct the mistakes made while filing the Form 945. Learn about Form 945-X and file using the instructions provided."
          keywords=""
          canonical="resources/employment-tax/form-945-x-instructions/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 945-X</span>
          </span>{' '}
          Filing Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
            textAlign: 'center',
          }}
        >
          Learn about Form 945-X and correct you form 945 error with Confidence
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=945xinstrucions`}
          >
            E-File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-945-x-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },

                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 945-X at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          Form 945-X (Adjusted Annual Return of Withheld Federal
                          Income Tax or Claim for Refund)
                        </span>
                        is filed with the IRS to correct administrative errors
                        on a previously filed <br />
                        <span style={{ fontWeight: 600 }}> Form 945</span>.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}> Due Date: </span>{' '}
                        Form 945-X should be filed
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          as soon as an error is identified
                        </span>
                        .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}> Penalties:</span>{' '}
                        Correcting errors promptly may help reduce or avoid{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          IRS penalties and interest,
                        </span>{' '}
                        especially for underreported taxes that are paid
                        immediately.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/employment-tax/e-file-irs-form-945-x/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS Form 945-X
                  </Link>{' '}
                  is used to correct the administrative mistakes made previously
                  while filing the{' '}
                  <Hidden smDown>
                    {' '}
                    <br />
                  </Hidden>
                  form 945. Administrative errors can happen when the filer
                  calculated the amount withheld wrongly and reported the
                  wrongly calculated amount to IRS through{' '}
                  <Link
                    href="/resources/employment-tax/form-945-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 945
                  </Link>
                  . This instruction will help you in understanding form 945-X
                  and how to file the form to IRS.
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>This instruction contains </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#purpose" sx={{ color: '#0202ff' }}>
                          Form 945-X and its purpose
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is new in filing form 945-X?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#type" sx={{ color: '#0202ff' }}>
                          Type of filing methods
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#important" sx={{ color: '#0202ff' }}>
                          Important terms used in this instruction
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#deadline" sx={{ color: '#0202ff' }}>
                          Understanding the deadlines
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#step" sx={{ color: '#0202ff' }}>
                          Step by step Guide to file form 945-X
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="purpose"
                >
                  Form 945-X and its purpose
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 945-X is the adjusted annual return of withheld federal
                  income tax or claim for refund form used by the payer who made
                  non payroll payment, to correct the mistakes made in the form
                  945. If Overreported tax, refund can be claimed. If
                  underreported tax, tax owed should be paid immediately.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 82,
                  }}
                  id="what"
                >
                  What is new in filing form 945-X?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  IRS has recently added electronic filing option for 945 form
                  correction. Now you can E-File{' '}
                  <Hidden smDown>
                    <br />
                  </Hidden>{' '}
                  Form 945-X to correct the mistakes made during filing Form 945
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="type"
                >
                  Type of filing methods
                </Typography>

                <Typography
                  variant="h3"
                  sx={{ fontWeight: 600, fontSize: '20px !important', mb: 0.6 }}
                >
                  Paper filing{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payer who needs to correct the mistake in form 945 can use
                  paper filing method to file{' '}
                  <Hidden smDown>
                    <br />
                  </Hidden>{' '}
                  form 945-X. But it is slow and tedious method.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 600, fontSize: '20px !important', mb: 0.6 }}
                >
                  Electronic Filing{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  While paper filing is an option IRS provide easy and fastest
                  way to correct the errors in form 945. Now user can
                  electronically{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-945-x/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 945-X
                  </Link>{' '}
                  and correct mistakes in previously filed 945 forms.{' '}
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body1">
                        File Form 945-X whenever you find errors in form 945
                        using TaxZerone
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=945xinstructions`}
                      >
                        E-File Today
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="important"
                >
                  Important terms used in this instruction
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Form 945-X has two terms that define whether you owe tax or
                  eligible to get a refund, Let’s explore the term
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Underreported{' '}
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  When filing form 945, if you reported the tax withheld or
                  backup withholding lesser than the amount you actually
                  withheld, it is called underreported. In this case you owe tax
                  to the IRS, and it should be paid immediately. Generally,
                  there will be no penalties if you corrected this error as soon
                  as you noticed.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Overreported
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  When filing form 945-X, if you reported the tax withheld or
                  backup withholding amount higher than the actually withheld
                  amount, you are eligible for credit or refund, and this is
                  called overreported. File form 945-X to correct the error and
                  you can also claim refund using{' '}
                  <Hidden smDown>
                    <br />
                  </Hidden>{' '}
                  945-X form.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="deadline"
                >
                  Understanding the deadlines
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To calculate the deadline, the first and foremost thing you
                  need to understand is Succeeding April 15 from the date Form
                  945 is filed will be considered as filing date by the
                  IRS.{' '}
                </Typography>
                <Box
                  sx={{
                    border: '1px solid #333',
                    p: 2,
                    borderRadius: 2,
                    mb: 3,
                  }}
                >
                  <Typography variant="body1">
                    <span style={{ fontWeight: 600 }}> Example:</span> If you
                    file Form 945 on January 25, 2025, the IRS will treat it as
                    filed on <br />
                    April 15, 2025.{' '}
                  </Typography>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Period of limitations
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  IRS consider the period of limitation as
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    The correction form 945-X should be filed within three years
                    of filing IRS 945 form or two years from the date of payment
                    of wrongly reported tax on form 945. This applies to the
                    Overreported employment taxes.{' '}
                  </li>
                  <li>
                    The corrections should be filed within three years if the
                    employment tax is underreported and tax owed should be paid
                    immediately.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="step"
                >
                  Guide to file form 945-X
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Follow these step-by-step instructions to file correction form
                  945-X online.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Filer Details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-945-x-filter-img.webp" />
                  <source srcSet="/assets/images/form-945-x-filter-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-945-x-filter-img.png"
                    loading="lazy"
                    alt="Form 945-X: Enter EIN, Name, and complete address"
                    width={614}
                    height={282}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the Employer Identification Number (EIN), Filer full
                  name, Trade name, and address of the entity with Zip code{' '}
                </Typography>

                <picture>
                  <source srcSet="/assets/images/form-945-x-year-img.webp" />
                  <source srcSet="/assets/images/form-945-x-year-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-945-x-year-img.png"
                    loading="lazy"
                    alt="Form 945-X: Enter the year for which the form 945 administrative error should be corrected."
                    width={266}
                    height={102}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the year for which the form 945 administrative error
                  should be corrected.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-945-x-discovered-img.webp" />
                  <source srcSet="/assets/images/form-945-x-discovered-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-945-x-discovered-img.png"
                    loading="lazy"
                    alt="Form 945-X: Enter the date on which the administrative error in the form 945 is first identified."
                    width={266}
                    height={102}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the date on which the administrative error in the form
                  945 is first identified.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Part 1: Choose the tax adjustment option
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-945-x-adjustment-img.webp" />
                  <source srcSet="/assets/images/form-945-x-adjustment-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-945-x-adjustment-img.png"
                    loading="lazy"
                    alt="Form 945-X Part 1 Lines 1 and 2: Check the box on Line 1 if you underreported the tax or Line 2 if you need a refund of the overreported tax or abatement of the tax amount."
                    width={662}
                    height={122}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Line 1:</span> Check this
                  box if you underreported the tax. Also check this box if you
                  overreported the taxes and wishes to adjust the excess amount
                  as tax credit in the next form 945 tax return filing.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Line 2:</span> Check this
                  box if you need refund of the overreported tax or abatement of
                  the tax amount. If you have underreported the taxes, don’t
                  check on this box.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Part 2: Correct the administrative error
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-945-x-correction-img.webp" />
                  <source srcSet="/assets/images/form-945-x-correction-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-945-x-correction-img.png"
                    loading="lazy"
                    alt="Form 945-X Part 2 Lines 3 to 5: Enter the corrected and previously reported amounts, calculating differences, and totaling them on Line 5."
                    width={660}
                    height={216}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Line 3:</span> Enter the
                  Correct federal tax withheld in Column 1, previously entered
                  amount in Colum 2 and difference between the Column 1 and
                  Column 2 should be entered in column 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 4:</span> Enter the
                  correct backup withholding amount in Column 1, previously
                  entered amount in Colum 2 and difference between the Column 1
                  and Column 2 should be entered in column 3
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, mt: 3, fontSize: 14.5 }}
                >
                  <b>Note:</b> Column 3 value in both line 3 and 4 should be
                  represented with  €˜-’ sign, if amount entered in Column 1 is
                  lesser than amount in column 2
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 5:</span> Enter the
                  total amount by combining the amount in Column 3 of Line 3 and
                  4
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Part 3: Explain the correction
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-945-x-overreported-img.webp" />
                  <source srcSet="/assets/images/form-945-x-overreported-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-945-x-overreported-img.png"
                    loading="lazy"
                    alt="Form 945-X Part 3 Line 6: Check the box, if the correction done on line 3 and line 4, have both underreported and overreported amount."
                    width={670}
                    height={44}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 6:</span> Check the
                  box, if the correction done on line 3 and line 4, have both
                  underreported and overreported amount.
                </Typography>

                <picture>
                  <source srcSet="/assets/images/form-945-x-explanation-img.webp" />
                  <source srcSet="/assets/images/form-945-x-explanation-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-945-x-explanation-img.png"
                    loading="lazy"
                    alt="Form 945-X Part 3 Line 7: Write the complete description of the correction mentioned in part 4, line 3 and 2."
                    width={653}
                    height={377}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 7:</span> Write the
                  complete description of the correction mentioned in part 4,
                  line 3 and 2. Mention the
                </Typography>
                <Box component="ol" sx={{ mb: 3 }}>
                  <li style={{ marginBottom: 16 }}>
                    Form 945-X line number(s) affected.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Date you discovered the error.
                  </li>
                  <li style={{ marginBottom: 16 }}>Amount of the error.</li>
                  <li style={{ marginBottom: 16 }}>Cause of the error.</li>
                </Box>
                <Box
                  sx={{
                    border: '1px solid #333',
                    p: 2,
                    borderRadius: 2,
                    mb: 3,
                  }}
                >
                  <Typography variant="body1">
                    <span style={{ fontWeight: 600 }}> Example:</span> There is
                    $1800 difference in Column 3 of Line 4 is due to the typo
                    error. Previously it is wrongly reported as 2000 on January
                    23, 2025, but the actual backup withholding is actually
                    $200. This error was found on March 3, 2025, and we like to
                    get the overreported taxes as refund.
                  </Typography>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Part 4: Sign the form
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-945-x-sign-here-img.webp" />
                  <source srcSet="/assets/images/form-945-x-sign-here-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-945-x-sign-here-img.png"
                    loading="lazy"
                    alt="Form 945-X Part 4: Enter your Sign, date, best daytime to make a call and enter the Preparer details."
                    width={662}
                    height={340}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The individual responsible for filing form 945-X should sign
                  the form along with your name, title, date and contact number.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the form is filled by paid preparer, enter the preparer
                  name, sign, address along with the Zip code, PTIN, Date, EIN,
                  and phone number.
                </Typography>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      // id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        IRS website.
                        <br />
                        For more information, check the IRS{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i945x.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          instructions for Form 945-X{' '}
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Made error while filing form 945?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Just file form 945-X with TaxZerone
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              correct the mistakes for <b> $6.99/Return </b>
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=945xinstructions`}
              >
                E-File Form 945-X Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form945XInstructions;
